import { PDFDocument, rgb } from 'pdf-lib'
import fontkit from '@pdf-lib/fontkit'
import bwipjs from 'bwip-js'
import { boxToCode } from '@wolf/barcodes'


// Custom fonts are required because pdf-lib's StandardFonts only support WinAnsi encoding,
// which cannot render special characters (e.g., Polish: ą, ę, ł, superscripts: ⁰, ¹, etc.)
// Embedding TTF fonts with fontkit enables full Unicode support
const FONT_URLS = {
  regular: 'https://static.wetaca.com/assets/fonts/wolf/Roboto-Regular.ttf',
  bold: 'https://static.wetaca.com/assets/fonts/wolf/Roboto-Bold.ttf'
}

let fontCache: { regular: ArrayBuffer; bold: ArrayBuffer } | null = null
const loadFonts = async (): Promise<{ regular: ArrayBuffer; bold: ArrayBuffer }> => {
  if (fontCache) return fontCache

  const [regular, bold] = await Promise.all([
    fetch(FONT_URLS.regular).then(res => res.arrayBuffer()),
    fetch(FONT_URLS.bold).then(res => res.arrayBuffer())
  ])

  fontCache = { regular, bold }
  return fontCache
}

type LabelData = {
  sourceId: number
  workspaceId: number
  deliveryGroupId: number
  boxId: number
  boxTypeName: string
  deliveryGroupIndex: number
  deliveryGroupTotalBoxes: number
  route: { day: number; route: string }
  deliveryOption: string
  address: {
    firstname: string
    lastname: string
    street: string
    number: string
    floor?: string | null
    zipcode: string | number
    city: string
    phone: string
  }
  pageWidth: number
  pageHeight: number
}

export const generateLabelPdf = async (labelData: LabelData): Promise<Uint8Array> => {
  // Create a new PDF document
  const pdfDoc = await PDFDocument.create()

  // Add a page with the same size as the uploaded PDF
  const page = pdfDoc.addPage([labelData.pageWidth, labelData.pageHeight])

  // Register fontkit for custom font embedding
  pdfDoc.registerFontkit(fontkit)
  const fonts = await loadFonts()
  const regularFont = await pdfDoc.embedFont(fonts.regular, { subset: true })
  const boldFont = await pdfDoc.embedFont(fonts.bold, { subset: true })

  const { width, height } = page.getSize()

  // Generate barcode
  const boxCode = {
    sourceId: labelData.sourceId,
    workspaceId: labelData.workspaceId,
    deliveryGroupId: labelData.deliveryGroupId,
    boxId: labelData.boxId
  }

  const codeText = boxToCode(boxCode)

  const options: bwipjs.ToBufferOptions = {
    bcid: 'datamatrix',
    text: codeText,
    scale: 3,
  }

  const canvas = document.createElement('canvas')
  bwipjs.toCanvas(canvas, options)
  const barcodeDataUrl = canvas.toDataURL('image/png')

  // Convert base64 to bytes
  const barcodeImageBytes = Uint8Array.from(
    atob(barcodeDataUrl.split(',')[1]),
    c => c.charCodeAt(0)
  )

  const barcodeImage = await pdfDoc.embedPng(barcodeImageBytes)

  // Calculate proportional sizes based on page dimensions
  // Base reference: A4 (595 x 842)
  const scaleFactor = width / 595
  const barcodeSize = 150 * scaleFactor
  const margin = 40 * scaleFactor
  const topY = height - margin

  // Font sizes scaled
  const largeFontSize = 20 * scaleFactor
  const mediumFontSize = 16 * scaleFactor
  const normalFontSize = 14 * scaleFactor
  const smallFontSize = 12 * scaleFactor

  // Draw corners info (top corners)
  page.drawText(`${labelData.deliveryGroupId}`, {
    x: margin,
    y: topY,
    size: largeFontSize,
    font: boldFont,
    color: rgb(0, 0, 0)
  })

  page.drawText(`${labelData.route.route} - ${labelData.deliveryOption}`, {
    x: width - (250 * scaleFactor),
    y: topY,
    size: normalFontSize,
    font: regularFont,
    color: rgb(0, 0, 0)
  })

  if (labelData.deliveryGroupTotalBoxes > 1) {
    page.drawText(`Bulto: ${labelData.deliveryGroupIndex}/${labelData.deliveryGroupTotalBoxes}`, {
      x: margin,
      y: topY - (30 * scaleFactor),
      size: normalFontSize,
      font: regularFont,
      color: rgb(0, 0, 0)
    })
  }

  // Draw barcode (center)
  const barcodeX = (width - barcodeSize) / 2
  const barcodeY = height / 2

  page.drawImage(barcodeImage, {
    x: barcodeX,
    y: barcodeY - (10 * scaleFactor),
    width: barcodeSize,
    height: barcodeSize,
  })

  const codeTextWidth = regularFont.widthOfTextAtSize(codeText, smallFontSize)
  page.drawText(codeText, {
    x: (width - codeTextWidth) / 2,
    y: barcodeY - (30 * scaleFactor),
    size: smallFontSize,
    font: regularFont,
    color: rgb(0, 0, 0)
  })

  // Draw delivery data (bottom) - centered horizontally
  const deliveryY = height / 2 - (100 * scaleFactor)

  const nameText = `${labelData.address.firstname} ${labelData.address.lastname}`
  const nameWidth = boldFont.widthOfTextAtSize(nameText, mediumFontSize)

  page.drawText(nameText, {
    x: (width - nameWidth) / 2,
    y: deliveryY,
    size: mediumFontSize,
    font: boldFont,
    color: rgb(0, 0, 0)
  })

  const streetText = `${labelData.address.street} ${labelData.address.number} ${labelData.address.floor ?? ''}`
  const streetWidth = regularFont.widthOfTextAtSize(streetText, normalFontSize)
  page.drawText(streetText, {
    x: (width - streetWidth) / 2,
    y: deliveryY - (25 * scaleFactor),
    size: normalFontSize,
    font: regularFont,
    color: rgb(0, 0, 0)
  })

  const cityText = `${labelData.address.zipcode} ${labelData.address.city}`
  const cityWidth = regularFont.widthOfTextAtSize(cityText, normalFontSize)
  page.drawText(cityText, {
    x: (width - cityWidth) / 2,
    y: deliveryY - (50 * scaleFactor),
    size: normalFontSize,
    font: regularFont,
    color: rgb(0, 0, 0)
  })

  const phoneText = `Tlfno. ${labelData.address.phone}`
  const phoneWidth = regularFont.widthOfTextAtSize(phoneText, normalFontSize)
  page.drawText(phoneText, {
    x: (width - phoneWidth) / 2,
    y: deliveryY - (75 * scaleFactor),
    size: normalFontSize,
    font: regularFont,
    color: rgb(0, 0, 0)
  })

  // Draw box info (bottom left)

  page.drawText(labelData.boxTypeName, {
    x: margin,
    y: deliveryY - (75 * scaleFactor),
    size: mediumFontSize,
    font: boldFont,
    color: rgb(0, 0, 0)
  })

  page.drawText(`B${labelData.boxId}`, {
    x: margin,
    y: deliveryY - (50 * scaleFactor),
    size: normalFontSize,
    font: regularFont,
    color: rgb(0, 0, 0)
  })

  const pdfBytes = await pdfDoc.save()

  return pdfBytes
}
